(function() {
  'use strict';

  var app = document.getElementById('my-esims-app');
  if (!app) return;

  var API_BASE = app.dataset.apiBase || '';
  var CUSTOMER_ID = app.dataset.customerId;
  var CUSTOMER_EMAIL = app.dataset.customerEmail;
  // 'token' mode = logged-out visitor arriving from a magic link in an email.
  var TOKEN_MODE = app.dataset.authMode === 'token';
  var LOGIN_PROMPT = document.getElementById('my-esims-login');
  var CURRENCY_SYMBOL = app.dataset.currencySymbol || '£';
  var CURRENCY_CODE = app.dataset.currencyCode || 'GBP';

  var state = {
    view: 'loading',
    email: CUSTOMER_EMAIL || '',
    sessionToken: localStorage.getItem('lt_esim_session') || null,
    esims: [],
    error: null,
    expandedIccid: null,
    detailsIccid: null,
    esimDetails: {},
    loadingDetails: null,
    topupPackages: {},
    loadingTopups: null,
    purchasingPkg: null
  };

  function readMagicToken() {
    try {
      return new URLSearchParams(window.location.search).get('token');
    } catch (e) {
      return null;
    }
  }

  // Drop the token from the address bar once it has been exchanged, so it is not
  // left in history, bookmarks or a shared screenshot. It is single-use anyway.
  function stripTokenFromUrl() {
    try {
      var url = new URL(window.location.href);
      if (!url.searchParams.has('token')) return;
      url.searchParams.delete('token');
      history.replaceState({}, document.title, url.pathname + url.search + url.hash);
    } catch (e) {
      /* history API unavailable - not worth failing the page over */
    }
  }

  // Hide rather than remove: if the token turns out to be expired we need to put
  // the login prompt back.
  function showApp() {
    if (LOGIN_PROMPT) LOGIN_PROMPT.hidden = true;
    app.hidden = false;
  }

  function showLoginPrompt() {
    app.hidden = true;
    if (LOGIN_PROMPT) LOGIN_PROMPT.hidden = false;
  }

  async function init() {
    var magicToken = TOKEN_MODE ? readMagicToken() : null;

    if (TOKEN_MODE) {
      // Nothing to go on: leave the login prompt exactly as the server rendered it.
      if (!magicToken && !state.sessionToken) return;
      showApp();
    }

    if (magicToken) {
      stripTokenFromUrl();
      if (await verifyMagicToken(magicToken)) return;
      // Token was invalid or expired. A stored session may still be good.
    }

    // Try existing session first
    if (state.sessionToken) {
      try {
        await loadEsims();
        return;
      } catch (e) {
        // Session expired or invalid, create a new one
        state.sessionToken = null;
        localStorage.removeItem('lt_esim_session');
      }
    }

    if (TOKEN_MODE) {
      // No Shopify customer to fall back on, so ask them to log in.
      showLoginPrompt();
      return;
    }
    await createCustomerSession();
  }

  // Exchanges a single-use magic-link token for a session, storing it exactly as
  // the customer-session path does. Returns true when the dashboard is showing.
  async function verifyMagicToken(token) {
    setState('loading');
    try {
      var res = await fetch(API_BASE + '/api/auth/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: token })
      });
      if (!res.ok) return false;
      var data = await res.json();
      if (!data.sessionToken) return false;
      state.sessionToken = data.sessionToken;
      state.email = data.email || state.email;
      localStorage.setItem('lt_esim_session', data.sessionToken);
      await loadEsims();
      return true;
    } catch (err) {
      return false;
    }
  }

  async function createCustomerSession() {
    setState('loading');
    try {
      var res = await fetch(API_BASE + '/api/auth/customer-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shopifyCustomerId: CUSTOMER_ID,
          email: CUSTOMER_EMAIL
        })
      });
      if (!res.ok) {
        var data = await res.json().catch(function() { return {}; });
        state.error = data.error || 'Failed to authenticate. Please try again.';
        setState('error');
        return;
      }
      var data = await res.json();
      state.sessionToken = data.sessionToken;
      state.email = data.email || CUSTOMER_EMAIL;
      localStorage.setItem('lt_esim_session', data.sessionToken);
      await loadEsims();
    } catch (err) {
      state.error = 'Network error. Please check your connection and try again.';
      setState('error');
    }
  }

  function setState(view) {
    state.view = view;
    render();
  }

  function render() {
    var html = '';
    switch (state.view) {
      case 'loading':
        html = renderLoading();
        break;
      case 'dashboard':
        html = renderDashboard();
        break;
      case 'error':
        html = renderError();
        break;
    }
    app.innerHTML = html;
    bindEvents();
  }

  // ---- Render Functions ----

  function renderLoading() {
    return '<div class="my-esims">' +
      '<div class="my-esims__skeleton">' +
        renderSkeletonCard() +
        renderSkeletonCard() +
        renderSkeletonCard() +
      '</div>' +
    '</div>';
  }

  function renderSkeletonCard() {
    return '<div class="my-esims__skeleton-card">' +
      '<div class="my-esims__skeleton-line my-esims__skeleton-line--title"></div>' +
      '<div class="my-esims__skeleton-line my-esims__skeleton-line--sub"></div>' +
      '<div class="my-esims__skeleton-actions">' +
        '<div class="my-esims__skeleton-line my-esims__skeleton-line--btn"></div>' +
        '<div class="my-esims__skeleton-line my-esims__skeleton-line--btn"></div>' +
      '</div>' +
    '</div>';
  }

  function renderDashboard() {
    var esims = state.esims;
    var html = '<div class="my-esims">' +
      '<div class="my-esims__dash-header">' +
        '<div class="my-esims__dash-info">' +
          '<h1 class="my-esims__dash-title">My eSIMs</h1>' +
          '<span class="my-esims__dash-email">' + escapeHtml(state.email) + '</span>' +
        '</div>' +
        '<button class="my-esims__signout" id="esim-signout">Sign out</button>' +
      '</div>';

    if (esims.length === 0) {
      html += renderEmpty();
    } else {
      html += '<p class="my-esims__dash-count">' + esims.length + ' eSIM' + (esims.length !== 1 ? 's' : '') + '</p>';
      html += '<div class="my-esims__cards">';
      for (var i = 0; i < esims.length; i++) {
        html += renderCard(esims[i]);
      }
      html += '</div>';
    }

    html += '</div>';
    return html;
  }

  function renderCard(esim) {
    var isExpanded = state.expandedIccid === esim.iccid;
    var isDetailsOpen = state.detailsIccid === esim.iccid;
    var cardClass = 'my-esims__card' +
      (isExpanded ? ' my-esims__card--topup-open' : '') +
      (isDetailsOpen ? ' my-esims__card--details-open' : '');
    var date = formatDate(esim.purchaseDate);

    var html = '<div class="' + cardClass + '" data-iccid="' + escapeHtml(esim.iccid) + '">' +
      '<div class="my-esims__card-body">' +
        '<div class="my-esims__card-header">' +
          '<h3 class="my-esims__card-name">' + escapeHtml(esim.packageName || 'eSIM Package') + '</h3>' +
          '<div class="my-esims__card-meta">' +
            'Order ' + escapeHtml(String(esim.orderNumber || '')) +
            '<span class="my-esims__card-meta-sep">|</span>' +
            date +
          '</div>' +
        '</div>' +
        '<div class="my-esims__card-actions">';

    html += '<button class="my-esims__btn my-esims__btn--secondary my-esims__btn--small esim-details-toggle" data-iccid="' + escapeHtml(esim.iccid) + '">' +
      (isDetailsOpen ? 'Close Details' : 'View Details & Usage') +
    '</button>';

    // Hide top-up button when the eSIM cannot be topped up. Status comes
    // from /api/esims (enriched per session) with fallback to esimDetails
    // populated by /details (loaded when "View Details" is clicked) or by
    // the /topup-packages 400 response.
    var status = esim.esimStatus || (state.esimDetails[esim.iccid] && state.esimDetails[esim.iccid].esimStatus);
    var blockedLabels = { USED_EXPIRED: 'Expired', UNUSED_EXPIRED: 'Expired', CANCEL: 'Cancelled', REVOKED: 'Revoked' };
    var isBlocked = !!blockedLabels[status];
    // Some plans (e.g. the South America SA-6 regional) don't support top-up at
    // all. /api/esims flags these with supportsTopUp:false so we can disable the
    // button on first paint instead of offering a dead button that returns an
    // empty package list. Default (field absent) = allowed; server gate backstops.
    var noTopUp = esim.supportsTopUp === false;

    if (isBlocked) {
      html += '<span class="my-esims__btn my-esims__btn--small my-esims__btn--disabled" title="' + blockedLabels[status] + ' eSIMs cannot be topped up">Top Up Unavailable</span>';
    } else if (noTopUp) {
      html += '<span class="my-esims__btn my-esims__btn--small my-esims__btn--disabled" title="This multi-country plan cannot be topped up. Buy a new eSIM when your data runs out.">No Top-Up</span>';
    } else {
      html += '<button class="my-esims__btn my-esims__btn--primary my-esims__btn--small esim-topup-toggle" data-iccid="' + escapeHtml(esim.iccid) + '">' +
          (isExpanded ? 'Close' : 'Top Up') +
        '</button>';
    }

    html += '</div>' +
      '</div>';

    // Details panel
    html += '<div class="my-esims__details-panel">' +
      '<div class="my-esims__details-inner">' +
        renderDetailsContent(esim.iccid) +
      '</div>' +
    '</div>';

    // Top-up panel
    html += '<div class="my-esims__topup-panel">' +
      '<div class="my-esims__topup-inner">' +
        '<h4 class="my-esims__topup-title">Available Top-Up Packages</h4>' +
        renderTopupContent(esim.iccid) +
      '</div>' +
    '</div>';

    html += '</div>';
    return html;
  }

  function renderDetailsContent(iccid) {
    if (state.loadingDetails === iccid) {
      return '<div class="my-esims__details-loading">' +
        '<div class="my-esims__spinner my-esims__spinner--small"></div>' +
        '<span class="my-esims__details-loading-text">Loading eSIM details...</span>' +
      '</div>';
    }

    var details = state.esimDetails[iccid];
    if (!details) {
      return '<div class="my-esims__details-loading">' +
        '<div class="my-esims__spinner my-esims__spinner--small"></div>' +
      '</div>';
    }

    if (details.error) {
      return '<p class="my-esims__details-error">Unable to load eSIM details. Please try again later.</p>';
    }

    var html = '';

    // Status badge
    var statusInfo = formatStatus(details.esimStatus);
    html += '<div class="my-esims__details-status">' +
      '<span class="my-esims__details-status-badge my-esims__details-status-badge--' + statusInfo.key + '">' +
        escapeHtml(statusInfo.label) +
      '</span>' +
    '</div>';

    // Stats grid
    html += '<div class="my-esims__details-grid">';

    // Data remaining
    if (details.totalVolume) {
      var remaining = formatBytes(details.remainingVolume);
      var total = formatBytes(details.totalVolume);
      var usedVolume = (details.totalVolume || 0) - (details.remainingVolume || 0);
      var usedPct = Math.round((usedVolume / details.totalVolume) * 100);
      html += '<div class="my-esims__details-stat">' +
        '<span class="my-esims__details-stat-label">Data Remaining</span>' +
        '<span class="my-esims__details-stat-value">' + remaining + ' / ' + total + '</span>' +
        '<div class="my-esims__details-bar">' +
          '<div class="my-esims__details-bar-fill" style="width:' + usedPct + '%"></div>' +
        '</div>' +
      '</div>';
    }

    // Expiry
    if (details.expiredTime) {
      html += '<div class="my-esims__details-stat">' +
        '<span class="my-esims__details-stat-label">Expires</span>' +
        '<span class="my-esims__details-stat-value">' + formatDate(details.expiredTime) + '</span>' +
      '</div>';
    } else if (details.totalDuration) {
      html += '<div class="my-esims__details-stat">' +
        '<span class="my-esims__details-stat-label">Validity</span>' +
        '<span class="my-esims__details-stat-value">' + details.totalDuration + ' ' + (details.durationUnit || 'DAY').toLowerCase() + 's</span>' +
      '</div>';
    }

    // ICCID
    html += '<div class="my-esims__details-stat my-esims__details-stat--full">' +
      '<div class="my-esims__details-field-header">' +
        '<span class="my-esims__details-stat-label">ICCID</span>' +
        '<button class="my-esims__copy-btn" data-copy="' + escapeHtml(iccid) + '" title="Copy">' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>' +
          '<span class="my-esims__copy-btn-label">Copy</span>' +
        '</button>' +
      '</div>' +
      '<span class="my-esims__details-stat-value my-esims__details-stat-value--mono">' + escapeHtml(iccid) + '</span>' +
    '</div>';

    html += '</div>';

    // Installation instructions (collapsible)
    if (details.smdpAddress || details.matchingId || details.apn) {
      html += '<details class="my-esims__details-section">' +
        '<summary class="my-esims__details-section-summary">Installation Instructions</summary>' +
        '<div class="my-esims__details-section-body">' +
        '<p class="my-esims__details-install-hint">Can\'t scan the QR code? Go to <strong>Settings \u2192 Mobile Data \u2192 Add eSIM \u2192 Enter Details Manually</strong> and enter the details below.</p>';

      if (details.smdpAddress) {
        html += '<div class="my-esims__details-field">' +
          '<div class="my-esims__details-field-header">' +
            '<span class="my-esims__details-field-label">SM-DP+ Address</span>' +
            '<button class="my-esims__copy-btn" data-copy="' + escapeHtml(details.smdpAddress) + '" title="Copy">' +
              '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>' +
              '<span class="my-esims__copy-btn-label">Copy</span>' +
            '</button>' +
          '</div>' +
          '<span class="my-esims__details-field-value my-esims__details-field-value--mono">' + escapeHtml(details.smdpAddress) + '</span>' +
        '</div>';
      }

      if (details.matchingId) {
        html += '<div class="my-esims__details-field">' +
          '<div class="my-esims__details-field-header">' +
            '<span class="my-esims__details-field-label">Activation Code</span>' +
            '<button class="my-esims__copy-btn" data-copy="' + escapeHtml(details.matchingId) + '" title="Copy">' +
              '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>' +
              '<span class="my-esims__copy-btn-label">Copy</span>' +
            '</button>' +
          '</div>' +
          '<span class="my-esims__details-field-value my-esims__details-field-value--mono">' + escapeHtml(details.matchingId) + '</span>' +
        '</div>';
      }

      if (details.apn) {
        html += '<div class="my-esims__details-field">' +
          '<div class="my-esims__details-field-header">' +
            '<span class="my-esims__details-field-label">APN</span>' +
            '<button class="my-esims__copy-btn" data-copy="' + escapeHtml(details.apn) + '" title="Copy">' +
              '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>' +
              '<span class="my-esims__copy-btn-label">Copy</span>' +
            '</button>' +
          '</div>' +
          '<span class="my-esims__details-field-value">' + escapeHtml(details.apn) + '</span>' +
        '</div>';
      }

      html += '</div></details>';
    }

    // QR Code (collapsible)
    if (details.qrCodeUrl) {
      html += '<details class="my-esims__details-section">' +
        '<summary class="my-esims__details-section-summary">Show QR Code</summary>' +
        '<div class="my-esims__details-qr-body">' +
          '<img src="' + escapeHtml(details.qrCodeUrl) + '" alt="eSIM QR Code" class="my-esims__details-qr-img" loading="lazy" />' +
          '<p class="my-esims__details-qr-hint">Scan this QR code from another device to install the eSIM.</p>' +
        '</div>' +
      '</details>';
    }

    return html;
  }

  function renderTopupContent(iccid) {
    if (state.loadingTopups === iccid) {
      return '<div class="my-esims__topup-loading"><div class="my-esims__spinner my-esims__spinner--small"></div></div>';
    }

    var packages = state.topupPackages[iccid];
    if (!packages) {
      return '<div class="my-esims__topup-loading"><div class="my-esims__spinner my-esims__spinner--small"></div></div>';
    }

    if (packages.length === 0) {
      return '<p class="my-esims__topup-empty">No top-up packages available for this eSIM.</p>';
    }

    var html = '<div class="my-esims__topup-packages">';
    for (var i = 0; i < packages.length; i++) {
      var pkg = packages[i];
      var isPurchasing = state.purchasingPkg === pkg.packageCode;
      html += '<div class="my-esims__topup-package">' +
        '<div class="my-esims__topup-pkg-info">' +
          '<span class="my-esims__topup-pkg-name">' + escapeHtml(pkg.name || pkg.packageCode) + '</span>' +
          '<span class="my-esims__topup-pkg-price">' + escapeHtml(CURRENCY_SYMBOL) + parseFloat(pkg.price || 0).toFixed(2) + '</span>' +
        '</div>' +
        '<button class="my-esims__btn my-esims__btn--primary my-esims__btn--small esim-buy-topup" data-iccid="' + escapeHtml(iccid) + '" data-package="' + escapeHtml(pkg.packageCode) + '"' + (isPurchasing ? ' disabled' : '') + '>' +
          (isPurchasing ? 'Processing...' : 'Buy') +
        '</button>' +
      '</div>';
    }
    html += '</div>';
    return html;
  }

  function renderEmpty() {
    return '<div class="my-esims__empty">' +
      '<div class="my-esims__empty-icon">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
          '<rect x="2" y="4" width="20" height="16" rx="2"/><path d="M7 15h0M2 9h20"/>' +
        '</svg>' +
      '</div>' +
      '<h3 class="my-esims__empty-title">No eSIMs found</h3>' +
      '<p class="my-esims__empty-desc">No eSIMs found for this account. If you recently made a purchase, it may take a few minutes to appear.</p>' +
      '<a href="/collections/all" class="my-esims__btn my-esims__btn--primary">Browse eSIMs</a>' +
    '</div>';
  }

  function renderError() {
    return '<div class="my-esims">' +
      '<div class="my-esims__header">' +
        '<div class="my-esims__brand">' +
          '<span>' + ((typeof window !== 'undefined' && window.THEME_BRAND && window.THEME_BRAND.name) || '') + '</span>' +
          '<span class="my-esims__brand-label">My eSIMs</span>' +
        '</div>' +
      '</div>' +
      '<div class="my-esims__error">' +
        '<div class="my-esims__error-icon">!</div>' +
        '<p class="my-esims__error-message">' + escapeHtml(state.error || 'Something went wrong. Please try again.') + '</p>' +
        '<button class="my-esims__btn my-esims__btn--primary" id="esim-try-again">Try Again</button>' +
      '</div>' +
    '</div>';
  }

  // ---- Event Binding ----

  function bindEvents() {
    var signoutBtn = document.getElementById('esim-signout');
    if (signoutBtn) {
      signoutBtn.addEventListener('click', logout);
    }

    var tryAgainBtn = document.getElementById('esim-try-again');
    if (tryAgainBtn) {
      tryAgainBtn.addEventListener('click', function() {
        state.error = null;
        init();
      });
    }

    var detailsToggles = document.querySelectorAll('.esim-details-toggle');
    for (var k = 0; k < detailsToggles.length; k++) {
      detailsToggles[k].addEventListener('click', handleDetailsToggle);
    }

    var topupToggles = document.querySelectorAll('.esim-topup-toggle');
    for (var i = 0; i < topupToggles.length; i++) {
      topupToggles[i].addEventListener('click', handleTopupToggle);
    }

    var buyBtns = document.querySelectorAll('.esim-buy-topup');
    for (var j = 0; j < buyBtns.length; j++) {
      buyBtns[j].addEventListener('click', handleBuyTopup);
    }

    var copyBtns = document.querySelectorAll('.my-esims__copy-btn');
    for (var c = 0; c < copyBtns.length; c++) {
      copyBtns[c].addEventListener('click', handleCopyClick);
    }
  }

  function handleCopyClick(e) {
    e.preventDefault();
    var btn = e.currentTarget;
    var text = btn.dataset.copy;
    var label = btn.querySelector('.my-esims__copy-btn-label');
    if (!text) return;
    navigator.clipboard.writeText(text).then(function() {
      btn.classList.add('my-esims__copy-btn--copied');
      if (label) label.textContent = 'Copied!';
      setTimeout(function() {
        btn.classList.remove('my-esims__copy-btn--copied');
        if (label) label.textContent = 'Copy';
      }, 1500);
    });
  }

  function handleDetailsToggle(e) {
    var iccid = e.currentTarget.dataset.iccid;
    if (state.detailsIccid === iccid) {
      state.detailsIccid = null;
      render();
    } else {
      state.detailsIccid = iccid;
      render();
      if (!state.esimDetails[iccid]) {
        loadEsimDetails(iccid);
      }
    }
  }

  function handleTopupToggle(e) {
    var iccid = e.currentTarget.dataset.iccid;
    if (state.expandedIccid === iccid) {
      state.expandedIccid = null;
      render();
    } else {
      state.expandedIccid = iccid;
      render();
      if (!state.topupPackages[iccid]) {
        loadTopupPackages(iccid);
      }
    }
  }

  function handleBuyTopup(e) {
    var iccid = e.currentTarget.dataset.iccid;
    var packageCode = e.currentTarget.dataset.package;
    purchaseTopup(iccid, packageCode);
  }

  // ---- API Calls ----

  async function loadEsims() {
    setState('loading');
    var res = await apiCall('GET', '/api/esims');
    if (!res.ok) {
      state.error = 'Failed to load your eSIMs. Please try again.';
      setState('error');
      return;
    }
    var data = await res.json();
    state.esims = data.esims || [];
    state.email = data.email || state.email;
    // Pre-populate the details cache from the enriched /api/esims payload so
    // the details panel skips its first round-trip when opened. /details is
    // still needed for QR code, activation code, etc.
    state.esims.forEach(function(esim) {
      if (esim.esimStatus) {
        state.esimDetails[esim.iccid] = state.esimDetails[esim.iccid] || {};
        state.esimDetails[esim.iccid].esimStatus = esim.esimStatus;
        state.esimDetails[esim.iccid].remainingVolume = esim.remainingVolume;
        state.esimDetails[esim.iccid].totalVolume = esim.totalVolume;
        state.esimDetails[esim.iccid].expiredTime = esim.expiredTime;
      }
    });
    setState('dashboard');
  }

  async function loadEsimDetails(iccid) {
    state.loadingDetails = iccid;
    render();
    try {
      var res = await apiCall('GET', '/api/esims/' + encodeURIComponent(iccid) + '/details');
      if (!res.ok) {
        state.esimDetails[iccid] = { error: true };
        state.loadingDetails = null;
        render();
        return;
      }
      var data = await res.json();
      state.esimDetails[iccid] = data.iccid ? data : { error: true };
    } catch (err) {
      state.esimDetails[iccid] = { error: true };
    }
    state.loadingDetails = null;
    render();
  }

  async function loadTopupPackages(iccid) {
    state.loadingTopups = iccid;
    render();
    try {
      var res = await apiCall('GET', '/api/esims/' + encodeURIComponent(iccid) + '/topup-packages');
      if (!res.ok) {
        var errData = await res.json().catch(function() { return {}; });
        if (errData.blocked || errData.expired) {
          // Server says this eSIM is blocked from top-ups. Close panel and
          // update state so the button flips to "Top Up Unavailable". Use the
          // canonical status from the new shape if provided; legacy responses
          // only sent { expired: true }, infer USED_EXPIRED in that case.
          state.esimDetails[iccid] = state.esimDetails[iccid] || {};
          state.esimDetails[iccid].esimStatus = errData.status || 'USED_EXPIRED';
          state.expandedIccid = null;
          state.loadingTopups = null;
          render();
          return;
        }
        state.topupPackages[iccid] = [];
        state.loadingTopups = null;
        render();
        return;
      }
      var data = await res.json();
      state.topupPackages[iccid] = data.packages || [];
    } catch (err) {
      state.topupPackages[iccid] = [];
    }
    state.loadingTopups = null;
    render();
  }

  async function purchaseTopup(iccid, packageCode) {
    state.purchasingPkg = packageCode;
    render();
    try {
      var res = await apiCall('POST', '/api/esims/' + encodeURIComponent(iccid) + '/topup', { packageCode: packageCode });
      if (!res.ok) {
        var data = await res.json().catch(function() { return {}; });
        state.purchasingPkg = null;
        render();
        alert(data.error || 'Failed to initiate top-up. Please try again.');
        return;
      }
      var data = await res.json();
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        state.purchasingPkg = null;
        render();
      }
    } catch (err) {
      state.purchasingPkg = null;
      render();
      alert('Network error. Please try again.');
    }
  }

  async function apiCall(method, path, body) {
    var headers = { 'Content-Type': 'application/json' };
    if (state.sessionToken) {
      headers['Authorization'] = 'Bearer ' + state.sessionToken;
    }
    var opts = { method: method, headers: headers };
    if (body) {
      opts.body = JSON.stringify(body);
    }
    var res = await fetch(API_BASE + path, opts);
    if (res.status === 401) {
      localStorage.removeItem('lt_esim_session');
      state.sessionToken = null;
      if (TOKEN_MODE) {
        // Magic-link visitor: there is no Shopify customer to silently re-auth
        // against, and the token was single-use. Send them back to the prompt.
        showLoginPrompt();
        throw new Error('Session expired');
      }
      // Session expired — try to create a new one transparently
      await createCustomerSession();
      throw new Error('Session refreshed');
    }
    return res;
  }

  function logout() {
    localStorage.removeItem('lt_esim_session');
    state.sessionToken = null;
    state.esims = [];
    state.expandedIccid = null;
    state.detailsIccid = null;
    state.esimDetails = {};
    state.topupPackages = {};
    if (TOKEN_MODE) {
      // No Shopify session to end - just drop back to the login prompt.
      showLoginPrompt();
      return;
    }
    window.location.href = '/account/logout';
  }

  // ---- Helpers ----

  function escapeHtml(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  function formatDate(dateStr) {
    if (!dateStr) return '';
    try {
      var d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return d.getDate() + ' ' + months[d.getMonth()] + ' ' + d.getFullYear();
    } catch (e) {
      return dateStr;
    }
  }

  function formatStatus(status) {
    var map = {
      'IN_USE': { key: 'active', label: 'Active' },
      'NOT_ACTIVE': { key: 'inactive', label: 'Not Activated' },
      'USED_UP': { key: 'depleted', label: 'Data Used Up' },
      'EXPIRED': { key: 'expired', label: 'Expired' }
    };
    return map[status] || { key: 'unknown', label: status || 'Unknown' };
  }

  function formatBytes(bytes) {
    if (!bytes && bytes !== 0) return 'N/A';
    var gb = bytes / (1024 * 1024 * 1024);
    if (gb >= 1) return gb.toFixed(gb % 1 === 0 ? 0 : 1) + ' GB';
    var mb = bytes / (1024 * 1024);
    return Math.round(mb) + ' MB';
  }

  init();
})();
